import React, { FC, useEffect, useMemo, useState } from 'react';
import styles from './index.module.scss';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import { CheckOutlined, CloseOutlined, FullscreenExitOutlined, FullscreenOutlined } from '@ant-design/icons';
import { apiV2ElectricMapDataSwsWeatherPost } from '@maxtropy/device-customer-apis-v2';
import { useRequest } from 'ahooks';
import { isNil } from 'lodash-es';
import { Switch } from '@maxtropy/components';
import { NavType, NavTypeDisplay, NavTypeTitleDisplay } from '../../const';
import elec_icon from '../../imgs/small-icon-electric.png';
import light_icon from '../../imgs/small-icon-light.png';
import maintenance_icon from '../../imgs/small-icon-maintenance@2x.png';
import environmental_icon from '../../imgs/small-icon-environment@2x.png';
import index_icon from '../../imgs/small-icon-index@2x.png';
import tube_icon from '../../imgs/small-icon-tube.png';
import gas_icon from '../../imgs/small-icon-gas@2x.png';
import { cleanupAllUnityBusinessPayloads } from '../UnityRenderMap/businessCleanup';

interface IHeaderProps {
  fullBtn: () => void;
  isFullScreen: boolean;
  isStandardSize: boolean;
  setLoadMap: React.Dispatch<React.SetStateAction<boolean>>;
  loadMap: boolean;
  setCurrentNav: React.Dispatch<React.SetStateAction<NavType>>;
  currentNav: NavType;
}

const Header: FC<IHeaderProps> = props => {
  const { fullBtn, isFullScreen, isStandardSize, setLoadMap, loadMap, setCurrentNav, currentNav } = props;
  const [time, setTime] = useState(() => dayjs().format('YYYY年M月D日 dddd HH:mm:ss'));
  const [hover, setHover] = useState(false);

  const [urlSearchParams, setUrlSearchParams] = useSearchParams();

  // 当前选中值（URL 是唯一数据源）
  const currentNavByUrl = urlSearchParams.get('currentNav');

  useEffect(() => {
    if (!currentNavByUrl) {
      setCurrentNav(NavType.EnergyCockpit);
    } else {
      setCurrentNav(Number(currentNavByUrl) as NavType);
    }
  }, [currentNavByUrl, setCurrentNav]);

  // 天气接口请求
  const { data: weatherData } = useRequest(() =>
    apiV2ElectricMapDataSwsWeatherPost({}).then(res => {
      return res;
    })
  );

  useEffect(() => {
    const timer = setInterval(() => {
      setTime(dayjs().format('YYYY年M月D日 dddd HH:mm:ss'));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // tabs切换
  const navItems = useMemo(
    () => [
      { type: NavType.EnergyCockpit, label: '能源概览', icon: index_icon },
      { type: NavType.PowerMap, label: '电力监控', icon: elec_icon },
      { type: NavType.WaterMap, label: '水务监控', icon: tube_icon },
      { type: NavType.IndustrialGasMap, label: '用气监控', icon: gas_icon },
      { type: NavType.LightingMap, label: '照明控制', icon: light_icon },
      { type: NavType.OperationOverview, label: '运维总览', icon: maintenance_icon },
      { type: NavType.EnvironmentalMonitor, label: '环保监控', icon: environmental_icon },
    ],
    []
  );

  const currentNavItem = useMemo(() => navItems.find(item => item.type === currentNav), [currentNav, navItems]);

  // 是否加载地图
  const onIsLoadMapChange = (checked: boolean) => {
    setLoadMap(checked);
  };

  // 处理tab点击
  const handleClick = async (item: any) => {
    if (item.type === NavType.OperationOverview) {
      window.open(`${window.JXDINTEGRATED}/inspectionV2/dashboard`, '_blank');
      return;
    }
    await cleanupAllUnityBusinessPayloads('[UnityMap]');
    setCurrentNav(item.type);
    setUrlSearchParams(prev => {
      const params = new URLSearchParams(prev);
      params.set('currentNav', item.type);
      return params;
    });
  };

  return (
    <div className={styles.header_area_box}>
      <div className={styles.date_box}>
        <div className={styles.time}>{time}</div>
        <div className={styles.weather}>
          <div className={styles.weatherRow}>
            <div>{`${!isNil(weatherData?.hourTemperature) ? weatherData?.hourTemperature : '--'}℃`}</div>
          </div>
        </div>
        {!isStandardSize && (
          <div className={styles.weather}>
            <span className={styles.value} style={{ marginRight: 6 }}>
              {weatherData?.windDirection ?? ''}
            </span>
            <span className={styles.value}>{weatherData?.windPower ?? ''}</span>
          </div>
        )}

        <div className={styles.loadMap}>
          <span>加载地图</span>
          <Switch
            checkedChildren={<CheckOutlined />}
            unCheckedChildren={<CloseOutlined />}
            onChange={onIsLoadMapChange}
            checked={loadMap}
          />
        </div>
      </div>
      <div className={styles.title_box}>
        <span className={`${styles.text} ${!isStandardSize ? styles.largeText : styles.smallText}`}>
          {NavTypeTitleDisplay[currentNav]}
        </span>
      </div>
      <div className={styles.set_box}>
        <div
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          className={`${styles.nav} ${styles.activeNav}`}
        >
          {currentNavItem?.icon && <img src={currentNavItem.icon} className={styles.icon} alt="" />}
          <span className={styles.label}>{NavTypeDisplay[currentNav]}</span>
          {hover && (
            <div className={styles.dropdown}>
              {navItems
                .filter(c => c.type !== currentNav)
                .map(item => (
                  <div key={item.type} className={styles.menu_item} onClick={() => handleClick(item)}>
                    <img src={item.icon} className={styles.icon} alt="" />
                    <span className={styles.label}>{item.label}</span>
                  </div>
                ))}
            </div>
          )}
        </div>
        <div className={styles.btn} onClick={fullBtn}>
          {isFullScreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
          &nbsp;
          <span className={styles.text}>{isFullScreen ? '退出' : '全屏'}</span>
          <div className={styles.bottomLine} />
        </div>
      </div>
    </div>
  );
};

export default Header;
